import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

import {
  APIgetRole,
  APIcreateRole,
  APIupdateRole,
  APIdeleteRole,
  APIupdateRoleCapabilities,
  APIgetRoleCapabilities,
} from '../../api/Roles';

import { showConfirmationModal } from '../../actions/App';

import AdminRoleEditHeader from '../../components/Admin/RoleEdit/Header';
import AdminRoleEditTabs from '../../components/Admin/RoleEdit/Tabs';
import AdminRoleEditTabContent from '../../components/Admin/RoleEdit/TabContent';

import {
  getTabUrlParam,
  updateTabUrlParam,
} from '../../helpers';
import {
  filterExcludingCapabilities,
  filterOutExcludingCapabilities,
} from '../../helpers/Auth';
import { addFlashMessage } from '../../helpers/FlashMessages';

import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';
import NothingFound from '../../components/NothingFound';
import GoBackToListButton from '../../components/GoBackToListButton';

class AdminRoleEdit extends Component {
  constructor(props) {
    super(props);

    this.state = {
      roleId: null,
      role: null,
      capabilities: {},
      errors: [],
      activeTab: getTabUrlParam() || 'details',
      is404: false,
      loading: true,
      saving: false,
      capabilitiesLoading: true,
      roleTouched: false,
      capabilitiesTouched: false,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleSwitchTab = this.handleSwitchTab.bind(this);
    this.handleCapabilitySwitch = this.handleCapabilitySwitch.bind(this);
    this.handleChangeCapabilityAttributes = this.handleChangeCapabilityAttributes.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  componentDidMount() {
    this.loadRole();
    this.loadCapabilities();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    const { roleId } = match.params;
    if (roleId && roleId !== prevProps.match.params.roleId) {
      this.loadRole();
    }
  }

  loadRole() {
    const { match } = this.props;
    const { roleId } = match.params;

    if (!roleId) {
      this.setState({
        role: {
          name: '',
          description: '',
        },
        loading: false,
      });
      return;
    }

    this.setState({
      roleId,
      loading: true,
    });

    APIgetRole(roleId).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({
          loading: false,
          role: data,
        });
      } else {
        this.setState({
          loading: false,
          is404: true,
        });
      }
    }).catch(() => {
      this.setState({
        loading: false,
        is404: true,
      });
    });
  }

  loadCapabilities() {
    this.setState({ capabilitiesLoading: true });

    APIgetRoleCapabilities().then((response) => {
      const { data } = response;
      if (data && data) {
        this.setState({ capabilities: data });
      }
      this.setState({ capabilitiesLoading: false });
    });
  }

  resetState() {
    this.setState({
      saving: false,
      errors: [],
      roleTouched: false,
      capabilitiesTouched: false,
    });
  }

  handleError() {
    const {
      intl,
    } = this.props;
    this.resetState();
    addFlashMessage({
      type: 'danger',
      text: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
    });
  }

  saveRole() {
    const {
      roleId,
      role,
    } = this.state;
    const {
      intl,
      history,
    } = this.props;

    if (roleId) {
      return APIupdateRole(role).catch(() => {
        this.handleError();
      });
    }

    return APIcreateRole(role).then((response) => {
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'The new role has been created' }),
      });
      const { data } = response;
      if (data && data.id) {
        history.push(`/admin/roles/${data.id}/edit`);
      }
    }).catch(() => {
      this.handleError();
    });
  }

  saveCapabilities() {
    const { role } = this.state;

    return APIupdateRoleCapabilities(role.id, {
      capabilities: role.capabilities,
      capability_attributes: role.capability_attributes,
    }).catch(() => {
      this.handleError();
    });
  }

  handleSave() {
    const {
      intl,
    } = this.props;
    const {
      role,
      roleId,
      roleTouched,
      capabilitiesTouched,
    } = this.state;
    const promises = [];

    if (!role.name) {
      this.setState({ errors: ['name'] });
      return;
    }

    this.setState({
      saving: true,
      errors: [],
    });

    if (roleTouched) {
      promises.push(this.saveRole());
    }
    if (capabilitiesTouched) {
      promises.push(this.saveCapabilities());
    }

    Promise.all(promises).then(() => {
      this.resetState();
      if (roleId) {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'The changes have been saved' }),
        });
      }
    });
  }

  handleSwitchTab(tab) {
    this.setState({ activeTab: tab });
    updateTabUrlParam(tab);
  }

  handleCapabilitySwitch(cap) {
    const { role } = this.state;

    const newRole = { ...role };
    if (role.capabilities.includes(cap)) {
      newRole.capabilities = newRole.capabilities.filter(c => (c !== cap));
      newRole.capabilities = filterOutExcludingCapabilities(cap, newRole.capabilities);
    } else {
      if (cap === 'edit_file' && !role.capabilities.includes('edit_people')) {
        newRole.capabilities.push('edit_people');
      }

      if (cap === 'use_image_editor' && !role.capabilities.includes('image_editor_save_versions')) {
        newRole.capabilities.push('image_editor_save_versions');
      }

      newRole.capabilities.push(cap);
      newRole.capabilities = filterExcludingCapabilities(cap, newRole.capabilities);
    }

    this.setState({
      role: newRole,
      capabilitiesTouched: true,
    });
  }

  handleChangeCapabilityAttributes(cap, attributes) {
    const { role } = this.state;

    const newRole = { ...role };
    if (!newRole.capability_attributes || newRole.capability_attributes.length === 0) {
      newRole.capability_attributes = {};
    }
    newRole.capability_attributes[cap] = attributes;

    this.setState({
      role: newRole,
      capabilitiesTouched: true,
    });
  }

  handleChange(e) {
    const {
      value,
      name,
    } = e.target;
    const { role } = this.state;

    if (name === 'name' || name === 'description') {
      role[name] = value;
      this.setState({
        role,
        roleTouched: true,
      });
    }
  }

  handleDelete() {
    const { role } = this.state;
    const {
      deleteRole,
      history,
    } = this.props;

    deleteRole(role, history);
  }

  render() {
    const {
      roleId,
      is404,
      loading,
    } = this.state;

    if (is404) {
      return (
        <NothingFound />
      );
    }

    return (
      <div className="main-content custom-role-edit-content">
        {loading ? (
          <Loading />
        ) : (
          <ErrorBoundary>
            <GoBackToListButton
              scope="roles"
            />
            <AdminRoleEditHeader
              roleId={roleId}
            />
            <AdminRoleEditTabs
              {...this.state}
              handleSwitchTab={this.handleSwitchTab}
            />
            <ErrorBoundary>
              <AdminRoleEditTabContent
                {...this.state}
                handleChange={this.handleChange}
                handleCapabilitySwitch={this.handleCapabilitySwitch}
                handleChangeCapabilityAttributes={this.handleChangeCapabilityAttributes}
                handleSave={this.handleSave}
                handleDelete={this.handleDelete}
              />
            </ErrorBoundary>
          </ErrorBoundary>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, props) => ({
  deleteRole: (role, history) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Are you sure you want to delete this role? This action can not be undone. If you have users associated with this role, they will become default users.' }),
      onConfirm: () => (
        APIdeleteRole(role.id).then(() => {
          history.push('/admin/roles');
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
});

AdminRoleEdit.propTypes = {
  intl: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  deleteRole: PropTypes.func.isRequired,
};

export default withRouter(injectIntl(connect(null, mapDispatchToProps)(AdminRoleEdit)));
